/**
 * MelConditionsList Component
 * Displays a bullet list of MEL conditions and operational notes
 */

import React from 'react';
import { cn } from '@/utils/cn';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface MelConditionsListProps {
  conditions: string[];
  operationalNotes?: string[];
  variant?: 'default' | 'compact' | 'card';
  className?: string;
}

export const MelConditionsList: React.FC<MelConditionsListProps> = ({
  conditions,
  operationalNotes = [],
  variant = 'default',
  className,
}) => {
  const hasConditions = conditions.length > 0;
  const hasNotes = operationalNotes.length > 0;

  if (!hasConditions && !hasNotes) {
    return (
      <div className={cn('text-foreground/50 text-sm italic', className)}>
        No special conditions or notes
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn('space-y-1', className)}>
        {conditions.map((condition, index) => (
          <div key={index} className="flex items-start gap-1.5 text-sm">
            <span className="text-yellow-500 dark:text-yellow-400 mt-0.5">*</span>
            <span className="text-foreground/80">{condition}</span>
          </div>
        ))}
        {operationalNotes.map((note, index) => (
          <div key={`note-${index}`} className="flex items-start gap-1.5 text-sm">
            <span className="text-blue-500 dark:text-blue-400 mt-0.5">i</span>
            <span className="text-foreground/60">{note}</span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={cn('space-y-4', className)}>
        {hasConditions && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <h4 className="font-medium text-yellow-800 dark:text-yellow-300">
                Conditions for Dispatch
              </h4>
            </div>
            <ul className="space-y-2">
              {conditions.map((condition, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-200"
                >
                  <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{condition}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasNotes && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-blue-800 dark:text-blue-300">
                Operational Notes
              </h4>
            </div>
            <ul className="space-y-2">
              {operationalNotes.map((note, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-blue-800 dark:text-blue-200"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 mt-2 flex-shrink-0" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn('space-y-4', className)}>
      {hasConditions && (
        <div>
          <h4 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
            Conditions for Dispatch
          </h4>
          <ul className="space-y-1.5 pl-6">
            {conditions.map((condition, index) => (
              <li
                key={index}
                className="list-disc text-sm text-foreground/80 marker:text-yellow-500 dark:marker:text-yellow-400"
              >
                {condition}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasNotes && (
        <div>
          <h4 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
            <InformationCircleIcon className="w-4 h-4 text-blue-500 dark:text-blue-400" />
            Operational Notes
          </h4>
          <ul className="space-y-1.5 pl-6">
            {operationalNotes.map((note, index) => (
              <li
                key={index}
                className="list-disc text-sm text-foreground/60 marker:text-blue-500 dark:marker:text-blue-400"
              >
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MelConditionsList;
